import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Pencil, Undo2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { Button } from "@/components/ui/Button";
import {
  getApplicationUlicpArea,
  resetApplicationUlicpGeometry,
  saveApplicationUlicpGeometry,
  setApplicationUlicpBuffer,
} from "../../api";
import { fmtArea, fmtDateTime } from "../../utils";
import { InfoHint } from "./InfoHint";

const HINT =
  "Domyślnie teren liczy się sam: pas o zadanej szerokości wokół projektowanej sieci, " +
  "docięty do działek wniosku z zapasem 0,10 m od granicy (przy samej sieci teren dochodzi " +
  "do granicy, bo linia musi się w nim zmieścić). Włącz „Edytuj obrys”, żeby dorysować " +
  "poligon, przesunąć wierzchołki albo skasować kawałek — od pierwszej ręcznej zmiany " +
  "obowiązuje TWÓJ obrys i to z niego liczy się powierzchnia. „Przelicz z pasa” wraca do " +
  "automatu. Ta sama geometria idzie do druku (pole 7.5), na mapę zakresu w pakiecie " +
  "i do skryptu „ulicp.lsp” w paczce formatek.";

type PmLayer = L.Layer & {
  pm?: { enable: (o?: unknown) => void; disable: () => void };
};

// Geoman w każdym komunikacie mówi o „warstwie” — przy rysowaniu obszaru na mapie to
// bełkot. Podmieniamy na słowa z tej roboty (obszar, wierzchołek, wycięcie); teksty są
// celowo neutralne, bo nadpisują polski słownik geomana dla całej aplikacji.
const TOOLBAR_PL = {
  tooltips: {
    firstVertex: "Kliknij, aby zacząć rysować obszar",
    continueLine: "Kliknij, aby dodać kolejny punkt",
    finishPoly: "Kliknij pierwszy punkt, aby zamknąć obszar",
    finishRect: "Kliknij, aby zamknąć prostokąt",
  },
  actions: {
    finish: "Zakończ",
    cancel: "Anuluj",
    removeLastVertex: "Cofnij ostatni punkt",
  },
  buttonTitles: {
    drawPolyButton: "Narysuj obszar",
    drawRectButton: "Narysuj prostokąt",
    editButton: "Przesuwaj wierzchołki",
    dragButton: "Przesuń cały obszar",
    cutButton: "Wytnij kawałek z obszaru",
    deleteButton: "Usuń obszar — włącz, potem kliknij obszar",
    removalTooltip: "Kliknij obszar, aby go usunąć",
  },
};

/** Teren inwestycji ULICP: pas wokół sieci ALBO obrys narysowany ręcznie na mapie.
 *
 *  Mapa jest interaktywna i edytowalna, bo 5 m na trasie kilkuset metrów na statycznym
 *  obrazku to włos — a projektant regularnie wie o terenie rzeczy, których nie widać
 *  w geometrii trasy (plac budowy, wjazd, obejście drzewa). */
export function UlicpAreaCard({ applicationId, disabled }: {
  applicationId: string;
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const queryKey = ["pp", "ulicp-area", applicationId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getApplicationUlicpArea(applicationId),
  });
  const [draft, setDraft] = useState("");
  const [edit, setEdit] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const contextRef = useRef<L.LayerGroup | null>(null);
  const terrainRef = useRef<L.FeatureGroup | null>(null);
  // Zapis w toku — bez tego pm:edit + pm:markerdragend potrafią wysłać dwa zapisy naraz.
  const savingRef = useRef(false);
  // Czy mapa była już wykadrowana — kadrujemy raz, nie po każdym zapisie.
  const fittedRef = useRef(false);

  useEffect(() => {
    if (data && document.activeElement?.getAttribute("data-ulicp-buffer") == null) {
      setDraft(String(data.buffer_m));
    }
  }, [data]);

  // Zapis w trakcie rysowania NIE odświeża całego wniosku: przeładowanie strony pakietu
  // montuje mapę od nowa i wyrzuca z miejsca, w którym akurat pracujesz. Powierzchnia na
  // wniosku to tylko cache — dociągamy go po wyjściu z trybu edycji.
  const onSaved = (fresh: unknown, { refreshApplication = true } = {}) => {
    qc.setQueryData(queryKey, fresh);
    if (refreshApplication) {
      qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
    }
  };

  const saveBuffer = useMutation({
    mutationFn: (width: number) => setApplicationUlicpBuffer(applicationId, width),
    onSuccess: (fresh) => onSaved(fresh),
  });
  const saveGeometry = useMutation({
    mutationFn: (geometry: unknown) => saveApplicationUlicpGeometry(applicationId, geometry),
    onSuccess: (fresh) => onSaved(fresh, { refreshApplication: false }),
  });
  const resetGeometry = useMutation({
    mutationFn: () => resetApplicationUlicpGeometry(applicationId),
    onSuccess: (fresh) => onSaved(fresh),
  });

  const applyBuffer = () => {
    if (!data) return;
    const width = Number(draft.replace(",", "."));
    if (!Number.isFinite(width)) return;
    if (width < data.buffer_min_m || width > data.buffer_max_m) return;
    if (width === data.buffer_m && !data.is_customized) return;
    saveBuffer.mutate(width);
  };

  // ── mapa ──
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    contextRef.current = L.layerGroup().addTo(map);
    // Teren siedzi we WŁASNEJ warstwie: odświeżenie działek i trasy nie gubi wtedy
    // uchwytów edycji (ten sam układ co bufor MDCP na mapie projektu).
    terrainRef.current = L.featureGroup().addTo(map);
    // Własne teksty zamiast standardowych geomanowych: tam wszystko nazywa się
    // „warstwą”, co przy rysowaniu terenu inwestycji nikomu nic nie mówi.
    map.pm.setLang("pl", TOOLBAR_PL, "pl");
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Najświeższe dane bez wpinania ich w zależności efektów — inaczej każdy zapis
  // przerysowywałby mapę (i gubił kadr oraz uchwyty edycji).
  const dataRef = useRef(data);
  dataRef.current = data;
  // Kontekst przerysowujemy tylko wtedy, gdy REALNIE się zmienił (inne działki / trasa),
  // a nie po każdym przeliczeniu powierzchni.
  const contextKey = `${data?.per_parcel.map((p) => p.parcel_id).join(",") ?? ""}|${data?.route ? 1 : 0}`;
  // W trybie edycji nie odtwarzamy warstw z serwera: to TY trzymasz obrys, a przerysowanie
  // po każdym zapisie kasowałoby uchwyty i wyrzucało mapę z miejsca, w którym pracujesz.
  const terrainKey = edit ? "edit" : `${data?.computed_at ?? ""}|${data?.area_m2 ?? ""}`;

  // kontekst: działki wniosku + trasa
  useEffect(() => {
    const map = mapRef.current;
    const group = contextRef.current;
    const data = dataRef.current;
    if (!map || !group || !data) return;
    group.clearLayers();
    for (const row of data.per_parcel) {
      if (!row.geometry) continue;
      L.geoJSON(row.geometry as never, {
        style: { color: "#555", weight: 1, fillColor: "#93c5fd", fillOpacity: 0.2 },
        pmIgnore: true,
        interactive: false,
      })
        .bindTooltip(`dz. ${row.short_number || row.teryt} — ${fmtArea(row.area_m2)}`)
        .addTo(group);
    }
    if (data.route)
      L.geoJSON(data.route as never, {
        style: { color: "#dc1f1f", weight: 3 },
        pmIgnore: true,
        interactive: false,
      }).addTo(group);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  // teren inwestycji + tryb edycji
  useEffect(() => {
    const map = mapRef.current;
    const group = terrainRef.current;
    const data = dataRef.current;
    if (!map || !group || !data) return;
    group.clearLayers();
    if (data.geometry) {
      L.geoJSON(data.geometry as never, {
        style: {
          color: "#15803d",
          weight: 2,
          fillColor: "#16a34a",
          fillOpacity: edit ? 0.2 : 0.35,
        },
        pmIgnore: !edit,
        interactive: edit,
      }).eachLayer((layer) => group.addLayer(layer));
    }

    // Kadrujemy TYLKO raz, przy pierwszym wejściu na kartę. Każde kolejne dopasowanie
    // wyrzucałoby użytkownika z miejsca, które właśnie ogląda albo edytuje.
    const raf = requestAnimationFrame(() => {
      map.invalidateSize();
      if (fittedRef.current) return;
      const bounds = group.getBounds();
      const target = bounds.isValid()
        ? bounds
        : data.route
          ? L.geoJSON(data.route as never).getBounds()
          : null;
      if (target?.isValid()) {
        map.fitBounds(target, { padding: [40, 40], maxZoom: 19 });
        fittedRef.current = true;
      }
    });

    if (!edit) {
      map.pm.removeControls();
      return () => cancelAnimationFrame(raf);
    }

    // Pasek Geomana: rysowanie NOWEGO poligonu, edycja wierzchołków, przeciąganie
    // i kasowanie. Każda z tych operacji kończy się zapisem CAŁEJ zawartości warstwy.
    map.pm.addControls({
      position: "topright",
      drawMarker: false,
      drawCircle: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: true,
      drawPolygon: true,
      drawText: false,
      editMode: true,
      dragMode: true,
      cutPolygon: true,
      removalMode: true,
      rotateMode: false,
    });
    map.pm.setGlobalOptions({ allowSelfIntersection: false, layerGroup: group });

    const persist = async () => {
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        const polygons: GeoJSON.Position[][][] = [];
        group.eachLayer((layer) => {
          // Leaflet NIE wyrzuca warstwy z FeatureGroup po jej usunięciu z mapy, więc bez
          // tego sprawdzenia skasowany obszar wracałby przy najbliższym zapisie.
          if (!map.hasLayer(layer)) return;
          const gj = (layer as L.Polygon).toGeoJSON?.() as GeoJSON.Feature | undefined;
          const geom = gj?.geometry;
          if (!geom) return;
          if (geom.type === "Polygon") polygons.push(geom.coordinates);
          else if (geom.type === "MultiPolygon") polygons.push(...geom.coordinates);
        });
        await saveGeometry.mutateAsync(
          polygons.length ? { type: "MultiPolygon", coordinates: polygons } : null,
        );
      } finally {
        savingRef.current = false;
      }
    };

    const bindLayer = (layer: L.Layer) => {
      (layer as PmLayer).pm?.enable({ allowSelfIntersection: false });
      layer.on("pm:edit", persist);
      layer.on("pm:markerdragend", persist);
      layer.on("pm:dragend", persist);
      layer.on("pm:remove", () => {
        group.removeLayer(layer);
        persist();
      });
    };

    const onCreate = (e: { layer: L.Layer }) => {
      // Świeżo narysowany obszar ma być od razu edytowalny i usuwalny.
      if (!group.hasLayer(e.layer)) group.addLayer(e.layer);
      bindLayer(e.layer);
      persist();
    };
    map.on("pm:create", onCreate);
    map.on("pm:remove", (e: { layer: L.Layer }) => {
      group.removeLayer(e.layer);
      persist();
    });
    map.on("pm:cut", persist);
    group.eachLayer(bindLayer);

    return () => {
      cancelAnimationFrame(raf);
      map.off("pm:create");
      map.off("pm:remove");
      map.off("pm:cut");
      group.eachLayer((layer) => {
        (layer as PmLayer).pm?.disable();
        layer.off("pm:edit");
        layer.off("pm:markerdragend");
        layer.off("pm:dragend");
        layer.off("pm:remove");
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terrainKey]);

  const busy = saveBuffer.isPending || saveGeometry.isPending || resetGeometry.isPending;
  const failed = saveBuffer.isError || saveGeometry.isError || resetGeometry.isError;

  return (
    <div className="space-y-2 border border-[var(--color-border)] p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="section-label">Teren inwestycji (ULICP)</div>
          <InfoHint text={HINT} />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label htmlFor="ulicp-buffer" className="text-[var(--color-text-secondary)]">
            Bufor od sieci
          </label>
          <input
            id="ulicp-buffer"
            data-ulicp-buffer
            type="number"
            className="input-base w-20 py-0.5 text-xs"
            min={data?.buffer_min_m ?? 0.5}
            max={data?.buffer_max_m ?? 50}
            step={0.5}
            value={draft}
            disabled={busy || disabled || isLoading}
            title={
              data?.is_customized
                ? "Obrys jest rysowany ręcznie — zmiana szerokości przeliczy teren od nowa z pasa"
                : undefined
            }
            onChange={(e) => setDraft(e.target.value)}
            onBlur={applyBuffer}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          <span className="text-[var(--color-text-secondary)]">m</span>
          {!disabled && (
            <Button
              size="sm"
              variant={edit ? "primary" : "secondary"}
              onClick={() => {
                setEdit((v) => {
                  // Wyjście z edycji to moment na dociągnięcie wniosku (powierzchnia
                  // w nagłówku, checklisty) — w trakcie rysowania byłoby to migotanie.
                  if (v) qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
                  return !v;
                });
              }}
              title="Rysuj nowe poligony, przesuwaj wierzchołki, kasuj kawałki — zapis po każdej zmianie"
            >
              <Pencil size={13} className="mr-1" />
              {edit ? "Zakończ edycję" : "Edytuj obrys"}
            </Button>
          )}
          {!disabled && data?.is_customized && (
            <Button
              size="sm"
              variant="ghost"
              loading={resetGeometry.isPending}
              title="Porzuć ręczny obrys i policz teren od nowa z pasa wokół sieci"
              onClick={() => resetGeometry.mutate()}
            >
              <Undo2 size={13} className="mr-1" /> Przelicz z pasa
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-base font-semibold">{data ? fmtArea(data.area_m2) : "—"}</span>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          powierzchnia terenu inwestycji (pole 7.5 druku) ·{" "}
          {data?.is_customized
            ? "obrys rysowany ręcznie"
            : `pas ${data?.buffer_m ?? "—"} m od sieci, docięty do działek`}
          {data?.computed_at && ` · przeliczono ${fmtDateTime(data.computed_at)}`}
        </span>
      </div>

      {edit && (
        <div className="text-[11px] text-[var(--color-text-secondary)]">
          Narzędzia są w prawym górnym rogu mapy: rysowanie obszaru, przesuwanie
          wierzchołków, przesuwanie całego obszaru, wycinanie kawałka i kosz (włącz kosz,
          potem kliknij obszar do usunięcia). Każda zmiana zapisuje się od razu —
          powierzchnia, druk i mapa w pakiecie idą za nią.
        </div>
      )}

      <div className="relative isolate z-0">
        <div
          ref={containerRef}
          className="w-full border border-[var(--color-border)]"
          style={{ height: 420, background: "#fff" }}
        />
        <div className="absolute bottom-2 left-2 z-[1000] bg-[var(--color-bg-surface)] border border-[var(--color-border)] shadow-sm px-2 py-1 font-mono text-[10px] text-[var(--color-text-secondary)] space-y-0.5">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5" style={{ background: "#dc1f1f" }} />
            trasa
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-2" style={{ background: "#16a34a", opacity: 0.5 }} />
            teren inwestycji
          </div>
        </div>
      </div>

      {data && data.per_parcel.length > 0 && (
        <div className="space-y-0.5 text-[11px]">
          {data.per_parcel.map((row) => (
            <div key={row.parcel_id} className="flex items-center gap-2">
              <span className="font-mono">{row.short_number || row.teryt}</span>
              <span className="text-[var(--color-text-muted)]">{row.obreb || "—"}</span>
              <span className="ml-auto">{fmtArea(row.area_m2)}</span>
              <span className="text-[var(--color-text-muted)] w-24 text-right">
                z {fmtArea(row.parcel_area_m2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {data?.warnings?.map((warning) => (
        <div key={warning} className="text-xs text-amber-600 flex items-start gap-1">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {warning}
        </div>
      ))}
      {failed && (
        <div className="text-xs text-red-600">
          Nie udało się zapisać terenu inwestycji — spróbuj ponownie.
        </div>
      )}
    </div>
  );
}
